import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import useMsal from '../../../auth/useMsal';
import useExternalMsal from '../../../auth/useExternalMsal';
import { useAppStore } from '../../../context/AppContext';
import { useAuthStore } from '../../../context/AuthContext';
import { SidePanelModulEnum } from '../../../enums/sidePanelModulEnum';
import { About, ClearCacheWrapper, Wrapper } from './SidePanelPopup.styles';
import { useRef } from 'react';
import { useAppStatus } from '../../../context/AppStatusContext';
import { useVesselStore } from '../../../context/VesselContext';
import { Curtain } from '../../../App.styles';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import SpinnerLoaderComponent from '../loader/SpinnerLoader';
import { RelativeWrapper } from '../../../styles/general.style';
import { useSWService } from '../../../api/sw/useSWService';
import FeaturesSelection from './FeaturesSelection';
import UserRoleSelection from './UserRoleSelection';
import ResolutionSelectionComponent from './ResolutionSelection';
import { OfflineSelectionComponent } from './OfflineSelection';
import { RoleTypeEnum } from 'src/enums/roleTypeEnum';
import useAppFeature from '../../../app/useAppFeature';
import { AppFeatureEnum } from '../../../enums/appFeatureEnum';
import { useNavigate } from 'react-router-dom';
import { ClearAppDataFailureStage, clearAppData } from '../../../utils/clearAppCache';
import { useToast } from '../../../app/useToast';

const SidePanelPopupComponent: React.FC = () => {
    const { isLoadingEndpoint } = useApiLoaderHelper();
    const { appsMenu, visibleSidePanelModuleIndex, setVisibleSidePanelModuleIndex } = useAppStore();
    const { loggedInRole, role, loggedInUser } = useAuthStore();
    const { logoutAzAd } = useMsal();
    const { logoutExternal, hasExternalAccountSession } = useExternalMsal();
    const { clearCache } = useSWService();
    const { toastError } = useToast();
    const logout = async (): Promise<void> => {
        try {
            await (hasExternalAccountSession() ? logoutExternal : logoutAzAd)();
        } catch (error) {
            console.error('[SidePanelPopup] Sign out failed.', error);
            toastError('Sign out failed. Please try again.', 8000);
        }
    };
    const ref = useRef<HTMLInputElement>(null);
    const { releaseNotes } = useAppStatus();
    const navigate = useNavigate();
    const { selectedVessel } = useVesselStore();
    const { isFeatureEnabled } = useAppFeature();
    const isAdminPageEnabled = isFeatureEnabled({ featureEnum: AppFeatureEnum.AdminPage, date: new Date() });
    const [releaseNotesModalOpen, setReleaseNotesModalOpen] = useState(false);

    useEffect(() => {
        const checkIfClickedOutside = (event) => {
            if (visibleSidePanelModuleIndex > 0 && !releaseNotesModalOpen && ref.current && !ref.current.contains(event.target)) {
                setVisibleSidePanelModuleIndex(-1);
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [visibleSidePanelModuleIndex, releaseNotesModalOpen]);

    const handleClearDataError = (stage: ClearAppDataFailureStage): void => {
        if (stage === 'cache') {
            toastError('Unable to clear the service-worker cache. The page will still reload.', 8000);
            return;
        }

        toastError('Local data was cleared, but sign out failed. Please try Sign out again.', 8000);
    };
    const handleClearCacheClick = () => clearAppData({ mode: 'cache', clearCache, onError: handleClearDataError });
    const handleClearAllLocalDataClick = () => {
        const logout = hasExternalAccountSession() ? logoutExternal : logoutAzAd;

        Modal.confirm({
            title: 'Clear all local data?',
            content: (
                <span>
                    This clears local application settings and signs you out. Your offline data and unsynced changes will be kept.
                </span>
            ),
            okText: 'Clear all local data',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () =>
                clearAppData({
                    mode: 'allLocalData',
                    clearCache,
                    logout,
                    onError: handleClearDataError,
                }),
        });
    };

    return (
        <>
            <Curtain></Curtain>
            <Wrapper ref={ref}>
                {visibleSidePanelModuleIndex === SidePanelModulEnum.AppsMenu && appsMenu && (
                    <>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            My Odfjell Apps
                        </h2>
                        <RelativeWrapper>
                            <>
                                {isLoadingEndpoint('getAppsDataHtml') && (
                                    <SpinnerLoaderComponent
                                        message={isLoadingEndpoint('getAppsDataHtml').message}
                                        clearDefaultText={true}
                                        isFeatureSpinner={true}
                                    />
                                )}
                            </>
                            <div dangerouslySetInnerHTML={{ __html: appsMenu.html }} />
                        </RelativeWrapper>
                    </>
                )}

                {visibleSidePanelModuleIndex === SidePanelModulEnum.About && loggedInUser && role && (
                    <About>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            About
                        </h2>
                        <div className="user-info-panel">
                            {isAdminPageEnabled && (
                                <div className="release-notes-container">
                                    <h3>Release notes</h3>
                                    {releaseNotes.length > 0 ? (
                                        <>
                                            <p style={{ margin: '0 0 2px 0', fontSize: '0.9rem', fontWeight: 500 }}>{releaseNotes[0].title}</p>
                                            <p style={{ margin: '0 0 8px 0', fontSize: '0.8rem', color: '#666' }}>
                                                {new Date(releaseNotes[0].releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                            <button
                                                onClick={() => setReleaseNotesModalOpen(true)}
                                                style={{
                                                    background: '#12427d',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '6px 14px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                View release notes
                                            </button>
                                        </>
                                    ) : (
                                        <p style={{ color: '#888', fontSize: '0.85rem' }}>No release notes available</p>
                                    )}
                                    <div style={{ marginBottom: '12px' }} />
                                </div>
                            )}
                            <div className="top-wrapper">
                                <h3>
                                    {loggedInUser?.fullName} ({loggedInRole?.roleType.toString()})
                                </h3>
                                <UserRoleSelection></UserRoleSelection>
                                <FeaturesSelection></FeaturesSelection>
                                <ResolutionSelectionComponent></ResolutionSelectionComponent>
                                {loggedInRole?.roleType === RoleTypeEnum.Admin && <OfflineSelectionComponent />}

                                {(loggedInUser?.fullName === 'Vegard Marken' ||
                                    loggedInUser?.fullName === 'Arnel Pinedo' ||
                                    loggedInUser?.fullName === 'Florian Pujol') &&
                                    selectedVessel?.vesselName === 'Bow Summer' && (
                                        <img className={'burger'} src={'assets/burger.jpg'} alt="Burger" loading="lazy" />
                                    )}
                            </div>
                            <div className="bottom-wrapper">
                                <button className="logout" onClick={logout}>
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </About>
                )}

                {visibleSidePanelModuleIndex === SidePanelModulEnum.Settings && (
                    <>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            Settings
                        </h2>
                        <ClearCacheWrapper>
                            <div className="action-section">
                                <button onClick={handleClearCacheClick}>Clear cache</button>
                                <span>Click to clear cache and reload data. This will also reload the page.</span>
                            </div>
                            <div className="action-section">
                                <button onClick={handleClearAllLocalDataClick}>Clear all local data</button>
                                <span title="Clears all local data including user login. This will sign you out.">
                                    Clears all local data including user login. This will sign you out.
                                </span>
                            </div>
                        </ClearCacheWrapper>
                        {loggedInRole?.roleType === RoleTypeEnum.Admin && isAdminPageEnabled && (
                            <div style={{ padding: '1rem', borderTop: '1px solid #eee' }}>
                                <h4>Admin</h4>
                                <button
                                    onClick={() => {
                                        setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE);
                                        navigate('/admin');
                                    }}
                                    style={{
                                        background: '#12427d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '0.5rem 1rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Admin Page
                                </button>
                            </div>
                        )}
                    </>
                )}
            </Wrapper>
            <Modal
                title={<span style={{ color: 'rgb(0, 68, 126)', fontWeight: 600 }}>What&apos;s New</span>}
                open={releaseNotesModalOpen}
                onCancel={() => setReleaseNotesModalOpen(false)}
                footer={null}
                maskClosable={false}
                width={620}
                styles={{
                    content: { padding: 0, background: 'rgb(239, 243, 247)' },
                    header: { borderBottom: '2px solid rgb(0, 68, 126)', padding: '16px 24px 12px', background: 'rgb(255, 255, 255)', borderRadius: '8px 8px 0 0' },
                    body: { maxHeight: '65vh', overflowY: 'auto', background: 'rgb(239, 243, 247)', padding: 0, borderRadius: '0 0 8px 8px' },
                }}
            >
                <div style={{ padding: '16px 20px' }}>
                {releaseNotes.slice(0, 3).map((note, index) => (
                    <div key={note.id} style={{
                        background: 'rgb(255, 255, 255)',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        marginBottom: index < 2 ? '12px' : 0,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                        borderLeft: index === 0 ? '4px solid rgb(0, 159, 223)' : '4px solid transparent',
                        opacity: index === 2 ? 0.75 : 1,
                    }}>
                        {/* Header row: label + date */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            {index === 0 ? (
                                <span style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: 'rgb(0, 68, 126)',
                                    background: 'rgb(239, 243, 247)',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase' as const,
                                    letterSpacing: '0.5px',
                                }}>Latest</span>
                            ) : <span />}
                            <span style={{ fontSize: '0.78rem', color: 'rgb(136, 136, 136)', letterSpacing: '0.2px' }}>
                                {new Date(note.releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </span>
                        </div>
                        {/* Title */}
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.95rem', fontWeight: 600, color: 'rgb(4, 38, 90)', lineHeight: 1.3 }}>
                            {note.title}
                        </h4>
                        {/* Separator */}
                        <div style={{ height: '1px', background: 'rgb(238, 238, 238)', marginBottom: '10px' }} />
                        {/* Content */}
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.88rem', lineHeight: 1.7, color: 'rgb(4, 38, 90)' }}>
                            {note.content.split('\n').filter(line => line.trim()).map((line, i) => (
                                <li key={i} style={{ marginBottom: '4px', paddingLeft: '4px' }}>
                                    <span style={{ color: 'rgb(0, 159, 223)', marginRight: '8px', fontWeight: 700 }}>•</span>
                                    {line.trim()}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                </div>
            </Modal>
        </>
    );
};

export default SidePanelPopupComponent;
